/**
 * Schedule III Financial Statements — Balance Sheet + Profit & Loss generated
 * from an actual EPM Trial Balance (pulled from Oracle Fusion, or uploaded),
 * using a maintained Chart-of-Accounts -> Schedule III line mapping.
 *
 * This is deliberately separate from the "Budgeted Financials" (Income
 * Statement / Balance Sheet) tabs in this same section — those are forecast
 * numbers built from the planning grids; this tab reports actuals, straight
 * from the ledger, in a statutory Schedule III layout.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { fmt } from "@/lib/api";
import { UploadSimple, DownloadSimple, CheckCircle, WarningCircle, X } from "@phosphor-icons/react";

const FORMAT_LABELS = { standard: "Standard — Companies Act Schedule III", banking: "Banking / NBFC Format" };
const MODES = [
  { key: "balance_sheet", label: "Balance Sheet" },
  { key: "profit_loss", label: "Profit & Loss" },
  { key: "schedules", label: "Supporting Schedules" },
  { key: "mapping", label: "Account Mapping" },
];

export default function Schedule3Reports() {
  const [formatType, setFormatType] = useState("standard");
  const [tbs, setTbs] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [mode, setMode] = useState("balance_sheet");
  const [tbId, setTbId] = useState("");
  const [tbIds, setTbIds] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadTbs = () => {
    api.get("/epm/trial-balances").then((r) => setTbs(r.data || []));
    api.get("/epm/ledgers").then((r) => setLedgers(r.data || []));
  };
  useEffect(loadTbs, []);

  useEffect(() => {
    if (tbs.length && !tbId) setTbId(tbs[0].id);
  }, [tbs]); // eslint-disable-line

  const ledgerName = (id) => ledgers.find((l) => l.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="schedule3-reports-tab">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm px-4 py-2.5 flex flex-wrap items-center gap-4">
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Format</span>
          <select value={formatType} onChange={(e) => setFormatType(e.target.value)} data-testid="s3-format-select"
            className="text-[13px] font-semibold text-[#0B1E3F] bg-transparent border-0 p-0 outline-none cursor-pointer">
            {Object.entries(FORMAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-1 border-l border-slate-200 pl-4">
          {MODES.map((m) => (
            <button key={m.key} onClick={() => setMode(m.key)} data-testid={`s3-mode-${m.key}`}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors ${
                mode === m.key ? "bg-[#0B1E3F] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <button onClick={() => setUploadOpen(true)} data-testid="s3-upload-tb-button"
          className="ml-auto inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-[#C8A24B] text-[#0B1E3F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors">
          <UploadSimple size={13} /> Upload Trial Balance
        </button>
      </div>

      {mode === "balance_sheet" && (
        <BalanceSheetTab formatType={formatType} tbs={tbs} tbId={tbId} setTbId={setTbId} ledgerName={ledgerName} />
      )}
      {mode === "profit_loss" && (
        <ProfitLossTab formatType={formatType} tbs={tbs} tbIds={tbIds} setTbIds={setTbIds} />
      )}
      {mode === "schedules" && (
        <SchedulesTab formatType={formatType} tbs={tbs} />
      )}
      {mode === "mapping" && (
        <MappingTab formatType={formatType} tbs={tbs} />
      )}

      {uploadOpen && (
        <UploadTbModal ledgers={ledgers} onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); loadTbs(); }} />
      )}
    </div>
  );
}

// --------------------------------------------------------- Statement grid --
function StatementGrid({ data, currency }) {
  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-auto max-h-[calc(100vh-320px)]" data-testid="s3-statement-grid">
      <table className="text-[12px] w-full border-collapse">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.12em] uppercase text-slate-600 bg-[#F1F5F9]">
            <th className="py-2.5 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Line</th>
            <th className="py-2.5 px-3 text-right sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200 min-w-[140px]">
              Amount {currency ? `(${currency})` : ""}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.sections.map((sec) => (
            <React.Fragment key={sec.key}>
              <tr><td colSpan={2} className="pt-3 pb-1 px-3 text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F]">{sec.label}</td></tr>
              {sec.rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-100" data-testid={`s3-row-${r.key}`}>
                  <td className="py-1.5 px-3 pl-6">{r.label}</td>
                  <td className={`py-1.5 px-3 text-right font-tabular-nums ${r.amount < 0 ? "text-red-600" : r.amount ? "" : "text-slate-300"}`}>
                    {fmt(r.amount)}
                  </td>
                </tr>
              ))}
              <tr className="border-b border-slate-200 bg-slate-50 font-bold" data-testid={`s3-section-total-${sec.key}`}>
                <td className="py-1.5 px-3">Total {sec.label}</td>
                <td className="py-1.5 px-3 text-right font-tabular-nums">{fmt(sec.total)}</td>
              </tr>
            </React.Fragment>
          ))}
          {data.grand_totals.map(([label, val], i) => (
            <tr key={label} className={`font-bold ${i === data.grand_totals.length - 1 ? "border-t-2 border-[#0B1E3F] bg-[#C8A24B]/10" : "bg-[#0B1E3F]/5"}`}
              data-testid={`s3-grand-total-${i}`}>
              <td className="py-2 px-3 text-[#0B1E3F]">{label}</td>
              <td className="py-2 px-3 text-right text-[#0B1E3F] font-tabular-nums">{fmt(val)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BalancedBadge({ balanced, unmappedCount, showBalance = true }) {
  if (unmappedCount > 0) {
    return (
      <span data-testid="s3-unmapped-badge" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-sm">
        <WarningCircle size={14} weight="fill" /> {unmappedCount} account(s) not yet mapped — go to Account Mapping
      </span>
    );
  }
  if (!showBalance) return null;
  return (
    <span data-testid="s3-balanced-badge" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-sm border ${
      balanced ? "text-green-700 bg-green-50 border-green-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
      {balanced ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
      {balanced ? "Statement balances" : "Does not balance — check mapping / period"}
    </span>
  );
}

// ------------------------------------------------------------- Balance Sheet
function BalanceSheetTab({ formatType, tbs, tbId, setTbId, ledgerName }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const tb = tbs.find((t) => t.id === tbId);

  useEffect(() => {
    if (!tbId) { setData(null); return; }
    let cancelled = false;
    setError(null);
    api.post("/epbcs/fin-reports/balance-sheet", { tb_id: tbId, format_type: formatType })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) { setData(null); setError(e?.response?.data?.detail || e.message); } });
    return () => { cancelled = true; };
  }, [tbId, formatType]);

  const download = async () => {
    try {
      const res = await api.get(`/epbcs/fin-reports/balance-sheet/export?tb_id=${tbId}&format_type=${formatType}`, { responseType: "blob" });
      const filename = res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || `Balance_Sheet_${tb?.period || ""}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setError(e?.response?.data?.detail || e.message || "Export failed"); }
  };

  return (
    <div className="space-y-3" data-testid="s3-balance-sheet-tab">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Trial Balance (as of)</span>
          <select value={tbId} onChange={(e) => setTbId(e.target.value)} data-testid="s3-bs-tb-select"
            className="text-[13px] font-semibold text-[#0B1E3F] bg-white border border-slate-200 rounded-sm px-2 py-1 min-w-[260px]">
            <option value="">Select a Trial Balance…</option>
            {tbs.map((t) => <option key={t.id} value={t.id}>{t.ledger_name} · {t.period} ({t.currency})</option>)}
          </select>
        </label>
        {data && <BalancedBadge balanced={data.balanced} unmappedCount={data.unmapped_accounts.length} />}
        {tbId && (
          <button onClick={download} data-testid="s3-bs-export-button"
            className="ml-auto inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-[#1F6B4A] text-[#1F6B4A] px-3.5 py-2 text-[12px] font-semibold rounded-sm hover:bg-green-50">
            <DownloadSimple size={13} /> Export to Excel
          </button>
        )}
      </div>
      {!tbId && <p className="text-sm text-slate-400">Select a Trial Balance to generate the Balance Sheet, or upload one above.</p>}
      {error && <p className="text-sm text-red-600" data-testid="s3-bs-error">{error}</p>}
      {tbId && <StatementGrid data={data} currency={tb?.currency} />}
    </div>
  );
}

// ------------------------------------------------------------- Profit&Loss
function ProfitLossTab({ formatType, tbs, tbIds, setTbIds }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tbIds.length) { setData(null); return; }
    let cancelled = false;
    setError(null);
    api.post("/epbcs/fin-reports/profit-loss", { tb_ids: tbIds, format_type: formatType })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) { setData(null); setError(e?.response?.data?.detail || e.message); } });
    return () => { cancelled = true; };
  }, [tbIds, formatType]);

  const toggle = (id) => setTbIds(tbIds.includes(id) ? tbIds.filter((x) => x !== id) : [...tbIds, id]);

  const download = async () => {
    try {
      const res = await api.get(`/epbcs/fin-reports/profit-loss/export?tb_ids=${tbIds.join(",")}&format_type=${formatType}`, { responseType: "blob" });
      const filename = res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || "Profit_and_Loss.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setError(e?.response?.data?.detail || e.message || "Export failed"); }
  };

  return (
    <div className="space-y-3" data-testid="s3-profit-loss-tab">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3">
        <p className="text-[10px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-2">
          Select one or more Trial Balance periods to sum for the Income Statement (e.g. all months of a quarter or year)
        </p>
        <div className="flex flex-wrap gap-2">
          {tbs.map((t) => (
            <label key={t.id} data-testid={`s3-pl-tb-${t.id}`}
              className={`cursor-pointer text-[11px] font-semibold px-2.5 py-1.5 rounded-sm border transition-colors ${
                tbIds.includes(t.id) ? "bg-[#0B1E3F] text-white border-[#0B1E3F]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              <input type="checkbox" className="hidden" checked={tbIds.includes(t.id)} onChange={() => toggle(t.id)} />
              {t.ledger_name} · {t.period}
            </label>
          ))}
          {!tbs.length && <p className="text-xs text-slate-400">No Trial Balances yet — upload one above.</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {data && <BalancedBadge balanced={data.balanced} unmappedCount={data.unmapped_accounts.length} showBalance={false} />}
        {tbIds.length > 0 && (
          <button onClick={download} data-testid="s3-pl-export-button"
            className="ml-auto inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-[#1F6B4A] text-[#1F6B4A] px-3.5 py-2 text-[12px] font-semibold rounded-sm hover:bg-green-50">
            <DownloadSimple size={13} /> Export to Excel
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-600" data-testid="s3-pl-error">{error}</p>}
      {tbIds.length > 0 && <StatementGrid data={data} currency={tbs.find((t) => t.id === tbIds[0])?.currency} />}
    </div>
  );
}

// -------------------------------------------------------- Supporting schedules
function SchedulesTab({ formatType, tbs }) {
  const [schedules, setSchedules] = useState([]);
  const [scheduleKey, setScheduleKey] = useState("");
  const [closingTbId, setClosingTbId] = useState("");
  const [showMovement, setShowMovement] = useState(false);
  const [openingTbId, setOpeningTbId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/epbcs/fin-reports/schedules?format_type=${formatType}`).then((r) => {
      setSchedules(r.data || []);
      if (r.data?.length && !scheduleKey) setScheduleKey(r.data[0].key);
    });
  }, [formatType]); // eslint-disable-line

  useEffect(() => {
    if (!scheduleKey || !closingTbId) { setData(null); return; }
    if (showMovement && !openingTbId) { setData(null); return; }
    let cancelled = false;
    setError(null);
    api.post("/epbcs/fin-reports/schedule-detail", {
      schedule_key: scheduleKey, format_type: formatType, closing_tb_id: closingTbId,
      opening_tb_id: showMovement ? openingTbId : "",
    }).then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) { setData(null); setError(e?.response?.data?.detail || e.message); } });
    return () => { cancelled = true; };
  }, [scheduleKey, closingTbId, showMovement, openingTbId, formatType]);

  const download = async () => {
    try {
      let url = `/epbcs/fin-reports/schedule-detail/export?schedule_key=${scheduleKey}&closing_tb_id=${closingTbId}&format_type=${formatType}`;
      if (showMovement && openingTbId) url += `&opening_tb_id=${openingTbId}`;
      const res = await api.get(url, { responseType: "blob" });
      const filename = res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || `Schedule_${scheduleKey}.xlsx`;
      const objUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = objUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(objUrl);
    } catch (e) { setError(e?.response?.data?.detail || e.message || "Export failed"); }
  };

  return (
    <div className="space-y-3" data-testid="s3-schedules-tab">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 flex flex-wrap items-end gap-4">
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Schedule</span>
          <select value={scheduleKey} onChange={(e) => setScheduleKey(e.target.value)} data-testid="s3-schedule-select"
            className="text-[13px] font-semibold text-[#0B1E3F] bg-white border border-slate-200 rounded-sm px-2 py-1 min-w-[220px]">
            {schedules.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">
            {showMovement ? "Closing Trial Balance" : "Trial Balance"}
          </span>
          <select value={closingTbId} onChange={(e) => setClosingTbId(e.target.value)} data-testid="s3-schedule-closing-tb-select"
            className="text-[13px] font-semibold text-[#0B1E3F] bg-white border border-slate-200 rounded-sm px-2 py-1 min-w-[220px]">
            <option value="">Select…</option>
            {tbs.map((t) => <option key={t.id} value={t.id}>{t.ledger_name} · {t.period}</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-[11px] font-semibold text-slate-600 pb-1.5 cursor-pointer">
          <input type="checkbox" checked={showMovement} onChange={(e) => setShowMovement(e.target.checked)} data-testid="s3-schedule-movement-toggle" />
          Show movement (opening vs closing)
        </label>
        {showMovement && (
          <label className="flex flex-col">
            <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Opening Trial Balance</span>
            <select value={openingTbId} onChange={(e) => setOpeningTbId(e.target.value)} data-testid="s3-schedule-opening-tb-select"
              className="text-[13px] font-semibold text-[#0B1E3F] bg-white border border-slate-200 rounded-sm px-2 py-1 min-w-[220px]">
              <option value="">Select…</option>
              {tbs.map((t) => <option key={t.id} value={t.id}>{t.ledger_name} · {t.period}</option>)}
            </select>
          </label>
        )}
        {closingTbId && data?.rows?.length > 0 && (
          <button onClick={download} data-testid="s3-schedule-export-button"
            className="ml-auto inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-[#1F6B4A] text-[#1F6B4A] px-3.5 py-2 text-[12px] font-semibold rounded-sm hover:bg-green-50">
            <DownloadSimple size={13} /> Export to Excel
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-600" data-testid="s3-schedule-error">{error}</p>}
      {!closingTbId && <p className="text-sm text-slate-400">Select a schedule and Trial Balance to view the breakdown.</p>}
      {data && !data.rows.length && <p className="text-sm text-slate-400" data-testid="s3-schedule-empty">No accounts mapped to "{data.label}" yet — map some in Account Mapping.</p>}
      {data && data.rows.length > 0 && (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-auto max-h-[calc(100vh-380px)]" data-testid="s3-schedule-grid">
          <table className="text-[12px] w-full border-collapse">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.12em] uppercase text-slate-600 bg-[#F1F5F9]">
                <th className="py-2.5 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Account / Sub-category</th>
                {data.has_movement && <th className="py-2.5 px-3 text-right sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Opening</th>}
                {data.has_movement && <th className="py-2.5 px-3 text-right sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Movement</th>}
                <th className="py-2.5 px-3 text-right sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Closing</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-100" data-testid={`s3-schedule-row-${r.key}`}>
                  <td className="py-1.5 px-3">{r.label}</td>
                  {data.has_movement && <td className="py-1.5 px-3 text-right font-tabular-nums">{fmt(r.opening)}</td>}
                  {data.has_movement && <td className="py-1.5 px-3 text-right font-tabular-nums">{fmt(r.movement)}</td>}
                  <td className="py-1.5 px-3 text-right font-tabular-nums">{fmt(r.closing)}</td>
                </tr>
              ))}
              <tr className="font-bold bg-slate-50 border-t border-slate-200" data-testid="s3-schedule-total">
                <td className="py-2 px-3">Total</td>
                {data.has_movement && <td className="py-2 px-3 text-right font-tabular-nums">{fmt(data.total.opening)}</td>}
                {data.has_movement && <td className="py-2 px-3 text-right font-tabular-nums">{fmt(data.total.movement)}</td>}
                <td className="py-2 px-3 text-right font-tabular-nums">{fmt(data.total.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------- Account mapping
function MappingTab({ formatType, tbs }) {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState({ balance_sheet: [], profit_loss: [] });
  const [tbForUnmapped, setTbForUnmapped] = useState("");
  const [unmapped, setUnmapped] = useState(null);
  const [draft, setDraft] = useState({ account_code: "", account_name: "", line_key: "", sub_category: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    api.get(`/epbcs/fin-reports/mapping?format_type=${formatType}`).then((r) => setRows(r.data || []));
  };
  useEffect(load, [formatType]); // eslint-disable-line
  useEffect(() => {
    Promise.all([
      api.get(`/epbcs/fin-reports/catalog?format_type=${formatType}&statement=balance_sheet`),
      api.get(`/epbcs/fin-reports/catalog?format_type=${formatType}&statement=profit_loss`),
    ]).then(([bs, pl]) => setCatalog({ balance_sheet: bs.data, profit_loss: pl.data }));
  }, [formatType]);

  const refreshUnmapped = () => {
    if (!tbForUnmapped) return;
    api.get(`/epbcs/fin-reports/unmapped?tb_id=${tbForUnmapped}&format_type=${formatType}`).then((r) => setUnmapped(r.data));
  };

  useEffect(() => {
    if (!tbForUnmapped) { setUnmapped(null); return; }
    refreshUnmapped();
  }, [tbForUnmapped, formatType]); // eslint-disable-line

  const lineOptions = useMemo(() => {
    const opts = [];
    [["balance_sheet", "Balance Sheet"], ["profit_loss", "Profit & Loss"]].forEach(([k, stLabel]) => {
      (catalog[k] || []).forEach((sec) => opts.push({ group: `${stLabel} — ${sec.label}`,
        lines: sec.lines.map((l) => ({ value: l.key, label: l.label })) }));
    });
    return opts;
  }, [catalog]);

  const save = async () => {
    if (!draft.account_code || !draft.line_key) return;
    setBusy(true); setMsg(null);
    try {
      await api.post("/epbcs/fin-reports/mapping", { ...draft, format_type: formatType });
      setDraft({ account_code: "", account_name: "", line_key: "", sub_category: "" });
      load();
      refreshUnmapped();
      setMsg({ ok: true, text: "Mapping saved" });
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/epbcs/fin-reports/mapping/${id}`); load(); refreshUnmapped(); };

  const uploadMapping = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("format_type", formatType);
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/epbcs/fin-reports/mapping/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ ok: data.failed === 0, text: `Created ${data.created}, updated ${data.updated}, failed ${data.failed}` });
      load();
      refreshUnmapped();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    const res = await api.get(`/epbcs/fin-reports/mapping/template?format_type=${formatType}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `schedule3_mapping_template_${formatType}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const lineLabel = (key) => {
    for (const grp of lineOptions) {
      const f = grp.lines.find((l) => l.value === key);
      if (f) return `${grp.group} — ${f.label}`;
    }
    return key;
  };

  return (
    <div className="space-y-4" data-testid="s3-mapping-tab">
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 space-y-3">
        <p className="text-[10px] uppercase font-bold tracking-[0.15em] text-slate-500">Map a GL account to a Schedule III line</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col">
            <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Account Code</span>
            <input value={draft.account_code} onChange={(e) => setDraft({ ...draft, account_code: e.target.value })}
              data-testid="s3-map-account-code" className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px] w-32" />
          </label>
          <label className="flex flex-col">
            <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Account Name</span>
            <input value={draft.account_name} onChange={(e) => setDraft({ ...draft, account_name: e.target.value })}
              data-testid="s3-map-account-name" className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px] w-56" />
          </label>
          <label className="flex flex-col">
            <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Schedule III Line</span>
            <select value={draft.line_key} onChange={(e) => setDraft({ ...draft, line_key: e.target.value })}
              data-testid="s3-map-line-select" className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px] w-72">
              <option value="">Select a line…</option>
              {lineOptions.map((grp) => (
                <optgroup key={grp.group} label={grp.group}>
                  {grp.lines.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="flex flex-col">
            <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Sub-category (optional, for Supporting Schedules)</span>
            <input value={draft.sub_category} onChange={(e) => setDraft({ ...draft, sub_category: e.target.value })}
              placeholder="e.g. Plant &amp; Machinery" data-testid="s3-map-sub-category"
              className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px] w-56" />
          </label>
          <button onClick={save} disabled={busy} data-testid="s3-map-save-button"
            className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3.5 py-2 text-[12px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
            Save Mapping
          </button>
          <button onClick={downloadTemplate} data-testid="s3-map-template-button"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-600 px-3 py-2 text-[11px] font-semibold rounded-sm hover:border-slate-400">
            <DownloadSimple size={13} /> Download Template
          </button>
          <label className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-600 px-3 py-2 text-[11px] font-semibold rounded-sm hover:border-slate-400 cursor-pointer">
            <UploadSimple size={13} /> Bulk Upload
            <input type="file" accept=".xlsx" className="hidden" data-testid="s3-map-bulk-upload-input"
              onChange={(e) => e.target.files[0] && uploadMapping(e.target.files[0])} />
          </label>
        </div>
        {msg && <p className={`text-[12px] font-semibold ${msg.ok ? "text-green-700" : "text-red-600"}`} data-testid="s3-map-message">{msg.text}</p>}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-[10px] uppercase font-bold tracking-[0.15em] text-slate-500">Check unmapped accounts in:</span>
          <select value={tbForUnmapped} onChange={(e) => setTbForUnmapped(e.target.value)} data-testid="s3-unmapped-tb-select"
            className="text-[12px] border border-slate-200 rounded-sm px-2 py-1">
            <option value="">Select a Trial Balance…</option>
            {tbs.map((t) => <option key={t.id} value={t.id}>{t.ledger_name} · {t.period}</option>)}
          </select>
        </div>
        {unmapped && (
          unmapped.unmapped.length === 0
            ? <p className="text-[12px] text-green-700 font-semibold" data-testid="s3-unmapped-none">All {unmapped.total_accounts} accounts in this Trial Balance are mapped.</p>
            : <p className="text-[12px] text-amber-700 font-semibold" data-testid="s3-unmapped-list">
                {unmapped.unmapped.length} unmapped: {unmapped.unmapped.join(", ")}
              </p>
        )}
      </div>

      <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-auto max-h-[calc(100vh-460px)]">
        <table className="text-[12px] w-full border-collapse">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.12em] uppercase text-slate-600 bg-[#F1F5F9]">
              <th className="py-2 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Account</th>
              <th className="py-2 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Name</th>
              <th className="py-2 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Schedule III Line</th>
              <th className="py-2 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200">Sub-category</th>
              <th className="py-2 px-3 sticky top-0 bg-[#F1F5F9] z-10 border-b border-slate-200 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`s3-map-row-${r.account_code}`}>
                <td className="py-1.5 px-3 font-semibold">{r.account_code}</td>
                <td className="py-1.5 px-3">{r.account_name}</td>
                <td className="py-1.5 px-3">{lineLabel(r.line_key)}</td>
                <td className="py-1.5 px-3 text-slate-500">{r.sub_category || "—"}</td>
                <td className="py-1.5 px-3 text-right">
                  <button onClick={() => remove(r.id)} data-testid={`s3-map-delete-${r.account_code}`} className="text-slate-400 hover:text-red-600"><X size={14} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="py-6 px-3 text-center text-slate-400">No accounts mapped yet for this format.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --------------------------------------------------------- Upload TB modal
function UploadTbModal({ ledgers, onClose, onDone }) {
  const [ledgerId, setLedgerId] = useState(ledgers[0]?.id || "");
  const [period, setPeriod] = useState("");
  const [currency, setCurrency] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Ledgers load asynchronously on the parent; if the modal is opened before
  // that fetch resolves, pick the first ledger as soon as it becomes available.
  useEffect(() => {
    if (!ledgerId && ledgers.length) setLedgerId(ledgers[0].id);
  }, [ledgers]); // eslint-disable-line

  const submit = async () => {
    if (!ledgerId || !period || !file) { setError("Ledger, period and file are required"); return; }
    setBusy(true); setError(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("ledger_id", ledgerId);
    fd.append("period", period);
    fd.append("currency", currency);
    try {
      await api.post("/epbcs/fin-reports/tb-quick-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="s3-upload-tb-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0B1E3F] uppercase tracking-wide">Upload Trial Balance</h3>
          <button onClick={onClose} data-testid="s3-upload-tb-close"><X size={18} /></button>
        </div>
        <p className="text-[11px] text-slate-500">
          CSV or Excel with columns: <code>account, account_name (optional), debit, credit</code>.
          This writes into the same Trial Balance store used by EPM — it will also appear there.
        </p>
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Ledger</span>
          <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)} data-testid="s3-upload-ledger-select"
            className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px]">
            <option value="">Select ledger…</option>
            {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.currency})</option>)}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Period (YYYY-MM)</span>
          <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-09" data-testid="s3-upload-period-input"
            className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px]" />
        </label>
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">Currency (optional — defaults to ledger currency)</span>
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="AED" data-testid="s3-upload-currency-input"
            className="border border-slate-200 rounded-sm px-2 py-1.5 text-[12px]" />
        </label>
        <label className="flex flex-col">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">File</span>
          <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="s3-upload-file-input" className="text-[12px]" />
        </label>
        {error && <p className="text-[12px] text-red-600 font-semibold" data-testid="s3-upload-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-[12px] font-semibold text-slate-500">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="s3-upload-submit-button"
            className="bg-[#0B1E3F] text-white px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
